import { useEffect, useState } from "react";
import { X, ZoomIn, Loader2 } from "lucide-react";
import { api } from "../lib/api";

export default function Gallery() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchPhotos = () => {
      api.get(`/gallery?ts=${Date.now()}`)
        .then((r) => { if (!cancelled) setPhotos(r.data || []); })
        .finally(() => { if (!cancelled) setLoading(false); });
    };

    fetchPhotos();

    // Refetch when user returns to the tab (e.g. after admin upload in another tab)
    const onVisibility = () => {
      if (document.visibilityState === "visible") fetchPhotos();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", fetchPhotos);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", fetchPhotos);
    };
  }, []);

  return (
    <section id="galerie" className="relative py-28 lg:py-40 bg-[#050505]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="reveal">
          <div className="text-xs uppercase tracking-[0.4em] text-[#3B82F6]">Galerie</div>
          <h2 className="font-display text-5xl md:text-7xl mt-4 leading-[0.9]">
            En lumière.<br />
            <span className="text-[#3B82F6]">En son.</span>
          </h2>
          <p className="text-zinc-400 max-w-xl mt-6">Quelques moments capturés en prestation. De l'arrivée du matériel jusqu'au dernier morceau.</p>
        </div>

        {loading ? (
          <div className="mt-14 flex items-center justify-center text-zinc-500 gap-2"><Loader2 className="animate-spin" size={16} /> Chargement…</div>
        ) : photos.length === 0 ? (
          <div className="mt-14 text-center text-zinc-500">Aucune photo pour le moment.</div>
        ) : (
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 auto-rows-[200px]">
            {photos.map((p, i) => {
              const spans = ["row-span-2", "row-span-1", "row-span-2", "row-span-1", "row-span-2", "row-span-1"];
              return (
                <button
                  key={p.id}
                  onClick={() => setActive(i)}
                  className={`group relative rounded-2xl overflow-hidden border border-white/10 hover:border-[#3B82F6]/60 transition-all animate-fade-in ${spans[i % spans.length]}`}
                  style={{ animationDelay: `${i * 60}ms` }}
                  data-testid={`gallery-item-${i}`}
                >
                  <img src={p.url} alt={p.caption || `Photo ${i + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                  <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between text-left">
                    <div className="text-xs uppercase tracking-[0.25em] text-zinc-200 truncate pr-2">{p.caption || ""}</div>
                    <span className="w-8 h-8 rounded-full glass flex items-center justify-center text-[#3B82F6] opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <ZoomIn size={14} />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {active !== null && photos[active] && (
        <div className="lightbox-backdrop" onClick={() => setActive(null)} data-testid="gallery-lightbox">
          <button
            onClick={(e) => { e.stopPropagation(); setActive(null); }}
            className="absolute top-6 right-6 w-12 h-12 rounded-full glass flex items-center justify-center text-white hover:text-[#3B82F6]"
            data-testid="gallery-lightbox-close"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
          <div className="max-w-5xl w-full px-6">
            <img src={photos[active].url} alt="" className="w-full max-h-[85vh] object-contain rounded-2xl" />
            {photos[active].caption && (
              <div className="mt-4 text-center text-sm uppercase tracking-[0.3em] text-zinc-400">{photos[active].caption}</div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
